import React, { useState, useEffect, useCallback } from 'react';
import { Button, Col, Form, Row } from "react-bootstrap";
import { useParams, useNavigate } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import ApiConfig from '../../api/ApiConfig';
import { simplePostCall } from '../../api/ApiServices';
import ScaleLoader from 'react-spinners/ScaleLoader';

const CreateExpense = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [accountTypes, setAccountTypes] = useState([]);
    const [expenseTypes, setExpenseTypes] = useState([]);

    // 1. Updated Initial State to match new keys
    const [formData, setFormData] = useState({
        expenses_number: "",
        account_type: "",
        date: new Date().toISOString().split('T')[0],
        list: [{ expenses: "", description: "", quantity: 1,amount:"" }] 
    });

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        const isUpdate = !!id;
        const url = isUpdate ? ApiConfig.expensesUpdate : ApiConfig.expensescreate;

        // 2. Updated Payload Construction
        const payload = {
            customer_id: Number(localStorage.getItem("customer_id")),
            api_key: localStorage.getItem("api_key"),
            user_id: Number(localStorage.getItem("user_id")),
            language: "en",
            expenses_number: formData.expenses_number,
            account_type: formData.account_type,
            date: formData.date,
            // Ensure the list only contains 'expenses', 'description', and 'quantity'
            list: formData.list.map(item => ({
                expenses: Number(item.expenses),
                description: item.description,
                quantity: Number(item.quantity),
                amount: Number(item.amount)
            }))
        };

        if (isUpdate) payload.id = id;

        try {
            const response = await simplePostCall(url, JSON.stringify(payload));
            if (response?.result) {
                toast.success(isUpdate ? "Expense Updated!" : "Expense Created!");
                navigate("/accountant/expenses");
            } else {
                toast.error(response?.message || "Operation failed");
            }
        } catch (error) {
            toast.error("Network Error");
        } finally {
            setLoading(false);
        }
    };

    // 3. Updated Helper for Dynamic List
    const handleListChange = (index, field, value) => {
        const newList = [...formData.list];
        newList[index][field] = value;
        setFormData({ ...formData, list: newList });
    };

    const addRow = () => setFormData({ 
        ...formData, 
        list: [...formData.list, { expenses: "", description: "", quantity: 1, amount: "" }]
    });

    // ... (fetchAccountTypes and fetchExpenseTypes remain the same as your original)
    const fetchAccountTypes = useCallback(async () => {
        try {
            const body = { customer_id: Number(localStorage.getItem("customer_id")), api_key: localStorage.getItem("api_key"), user_id: Number(localStorage.getItem("user_id")), name_search: "", limit: 1000, page: 1 };
            const res = await simplePostCall(ApiConfig.expensesaccounttype, JSON.stringify(body));
            if (res?.result) setAccountTypes(res.data || []);
        } catch (err) { console.error(err); }
    }, []);

    const fetchExpenseTypes = useCallback(async () => {
        try {
            const body = { customer_id: Number(localStorage.getItem("customer_id")), api_key: localStorage.getItem("api_key"), user_id: Number(localStorage.getItem("user_id")), price_search: "", name_search: "" };
            const res = await simplePostCall(ApiConfig.expensestype, JSON.stringify(body));
            if (res?.result) setExpenseTypes(res.data || []);
        } catch (err) { console.error(err); }
    }, []);

    useEffect(() => {
        fetchAccountTypes();
        fetchExpenseTypes();
    }, [fetchAccountTypes, fetchExpenseTypes]);

    return (
        <div className="income-type-container">
            <ToastContainer />
            <div className="top-header-section" style={{ height: "55px" }}>
                <h1 className="page-title-text">{id ? "Update" : "Create"} Expenses</h1>
            </div>

            <div className="bg-white rounded shadow-sm">
                {loading && !formData.expenses_number ? <div className="text-center p-2"><ScaleLoader color="#F4BB44" /></div> : (
                    <Form className=' p-3' onSubmit={handleSubmit}>
                        <Row className="g-3 mb-4">
                            <Col md={4}>
                                <Form.Label>Expenses Number *</Form.Label>
                                <Form.Control 
                                    required 
                                    value={formData.expenses_number} 
                                    onChange={(e) => setFormData({...formData, expenses_number: e.target.value})} 
                                />
                            </Col>
                            <Col md={4}>
                                <Form.Label>Account Type *</Form.Label>
                                <Form.Select 
                                    required 
                                    value={formData.account_type} 
                                    onChange={(e) => setFormData({...formData, account_type: e.target.value})}
                                >
                                    <option value="">Select</option>
                                    {accountTypes.map(t => (
                                        <option key={t.expenses_id} value={t.expense_account_type_id}>
                                            {t.expense_account_type_name}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Col>
                            <Col md={4}>
                                <Form.Label>Date *</Form.Label>
                                <Form.Control 
                                    type="date" 
                                    value={formData.date} 
                                    onChange={(e) => setFormData({...formData, date: e.target.value})} 
                                />
                            </Col>
                        </Row>

                        <hr />

                        {formData.list.map((row, index) => (
                            <Row key={index} className="g-3 mb-2 align-items-end">
                                <Col md={3}>
                                    <Form.Label>Expense Item</Form.Label>
                                    <Form.Select
                                        required
                                        value={row.expenses}
                                        onChange={(e) => handleListChange(index, 'expenses', e.target.value)}
                                    >
                                        <option value="">Select</option>
                                        {expenseTypes.map(et => (
                                            <option key={et.expense_type_id} value={et.expense_type_id}>
                                                {et.expense_type_name}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </Col>
                                <Col md={3}>
                                    <Form.Label>Description</Form.Label>
                                    <Form.Control
                                        value={row.description}
                                        onChange={(e) => handleListChange(index, 'description', e.target.value)}
                                    />
                                </Col>
                                <Col md={2}>
                                    <Form.Label>Qty</Form.Label>
                                    <Form.Control
                                        type="number"
                                        value={row.quantity}
                                        onChange={(e) => handleListChange(index, 'quantity', e.target.value)}
                                    />
                                </Col>
                                <Col md={2}>
                                    <Form.Label>Amount</Form.Label>
                                    <Form.Control
                                        type="number"
                                        required
                                        value={row.amount}
                                        onChange={(e) => handleListChange(index, 'amount', e.target.value)}
                                    />
                                </Col>
                                <Col md={2}>
                                    {index === formData.list.length - 1 && (
                                        <Button onClick={addRow} className="text-white" style={{ backgroundColor: "#5db2e8", border: "none" }}>+</Button>
                                    )}
                                </Col>
                            </Row>
                        ))}

                        <div className="mt-4 d-flex justify-content-end">
                            <Button type="submit" disabled={loading} style={{ backgroundColor: "#5db2e8", border: "none" }}>
                                {loading ? "Processing..." : (id ? "Update Expense" : "Create Expense")}
                            </Button>
                        </div>
                    </Form>
                )}
            </div>
        </div>
    );
};

export default CreateExpense;